import { DollarSign, TrendingUp, TrendingDown, Target, AlertTriangle, Wallet, PiggyBank } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { FinancialMetrics } from "@/hooks/gym-stats/useGymFinancialMetrics";
import { MetricConfig } from "@/hooks/gym-stats/useGymMetricsConfig";
import { DroppableArea } from "./DraggableMetricsGrid";
import { DraggableKPICard } from "./DraggableKPICard";

interface FinancialKPICardsProps {
  metrics: FinancialMetrics;
  enabledMetrics: MetricConfig[];
  isDraggable?: boolean;
  isEditMode?: boolean;
}

interface KPICardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  iconBg: string;
  trend?: "up" | "down" | "neutral";
}

const KPICard = ({ title, value, subtitle, icon, iconBg, trend }: KPICardProps) => {
  return (
    <Card className="border-border/50 bg-card/50 backdrop-blur-sm hover:bg-card/80 transition-colors h-full">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold text-foreground">{value}</p>
            {subtitle && (
              <div className="flex items-center gap-1">
                {trend === "up" && <TrendingUp className="h-3 w-3 text-green-500" />}
                {trend === "down" && <TrendingDown className="h-3 w-3 text-red-500" />}
                <span className={cn(
                  "text-xs",
                  trend === "up" ? "text-green-500" : trend === "down" ? "text-red-500" : "text-muted-foreground"
                )}>
                  {subtitle}
                </span>
              </div>
            )}
          </div>
          <div className={cn("p-3 rounded-xl", iconBg)}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const FinancialKPICards = ({ metrics, enabledMetrics, isDraggable = true, isEditMode = false }: FinancialKPICardsProps) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getKPIConfig = (id: string) => {
    const configs: Record<string, { title: string; value: string; subtitle?: string; icon: React.ReactNode; iconBg: string; trend?: "up" | "down" | "neutral" }> = {
      totalRevenue: {
        title: "Ingresos Totales",
        value: formatCurrency(metrics.totalRevenue),
        subtitle: "Histórico acumulado",
        icon: <DollarSign className="h-6 w-6 text-green-500" />,
        iconBg: "bg-green-500/10",
      },
      maxPayment: {
        title: "Pago Más Alto",
        value: formatCurrency(metrics.maxPayment),
        subtitle: `Mínimo: ${formatCurrency(metrics.minPayment)}`,
        icon: <TrendingUp className="h-6 w-6 text-emerald-500" />,
        iconBg: "bg-emerald-500/10",
      },
      revenuePerUser: {
        title: "Ingreso por Usuario",
        value: formatCurrency(metrics.revenuePerUser),
        subtitle: "Promedio histórico",
        icon: <Wallet className="h-6 w-6 text-blue-500" />,
        iconBg: "bg-blue-500/10",
      },
      delinquencyRate: {
        title: "Tasa de Morosidad",
        value: `${metrics.delinquencyRate.toFixed(1)}%`,
        subtitle: "Usuarios con membresía vencida",
        icon: <AlertTriangle className="h-6 w-6 text-amber-500" />,
        iconBg: "bg-amber-500/10",
        trend: metrics.delinquencyRate > 20 ? "down" : "neutral",
      },
      monthlyProjection: {
        title: "Proyección del Mes",
        value: formatCurrency(metrics.monthlyProjection),
        subtitle: "Estimación según tendencia",
        icon: <Target className="h-6 w-6 text-purple-500" />,
        iconBg: "bg-purple-500/10",
        trend: "up",
      },
      recoverableRevenue: {
        title: "Ingresos Recuperables",
        value: formatCurrency(metrics.recoverableRevenue),
        subtitle: "Membresías por vencer (30 días)",
        icon: <PiggyBank className="h-6 w-6 text-cyan-500" />,
        iconBg: "bg-cyan-500/10",
      },
    };
    return configs[id];
  };

  if (enabledMetrics.length === 0) return null;

  if (!isDraggable) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {enabledMetrics.map(metric => {
          const config = getKPIConfig(metric.id);
          if (!config) return null;
          return <KPICard key={metric.id} {...config} />;
        })}
      </div>
    );
  }

  return (
    <DroppableArea droppableId="financial-kpi-cards" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" isEditMode={isEditMode}>
      {enabledMetrics.map((metric, index) => {
        const config = getKPIConfig(metric.id);
        if (!config) return null;
        return (
          <DraggableKPICard key={metric.id} id={metric.id} index={index} isEditMode={isEditMode}>
            <KPICard {...config} />
          </DraggableKPICard>
        );
      })}
    </DroppableArea>
  );
};
